import { useEffect, useState } from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Formik, Form, Field, FormikProps } from "formik";
import * as Yup from "yup";
import { useFirebaseAuth } from "../hooks/useFirebaseAuth";
import { fetchDocuments } from "../utils/FetchDocuments";
import { LoanDetail, UploadedFile } from "../types/loan";
import { DocumentUploadSection } from "./DocumentUploadSection";
import {
  commonButtonProps,
  commonDateFieldProps,
  commonTextFieldProps,
} from "../utils/styles";

const validationSchema = Yup.object().shape({
  type: Yup.string().required("Loan type is required"),
  otherType: Yup.string().when("type", ([type], schema) =>
    type === "other"
      ? schema.required("Other type is required")
      : schema.notRequired()
  ),
  totalCost: Yup.number()
    .required("Total cost is required")
    .min(0, "Must be positive")
    .max(
      Yup.ref("vehicleCost"),
      "Total cost cannot be greater than vehicle cost"
    ),
  downPayment: Yup.number()
    .required("Down payment is required")
    .min(0, "Must be positive")
    .max(
      Yup.ref("totalCost"),
      "Down payment cannot be greater than total cost"
    ),
  amount: Yup.number()
    .required("Loan amount is required")
    .min(0, "Must be positive")
    .max(Yup.ref("totalCost"), "Loan amount cannot be greater than total cost"),
  duration: Yup.number()
    .required("Loan duration is required")
    .min(1, "Must be at least 1 month"),
  emi: Yup.number().required("EMI is required").min(0, "Must be positive"),
  startDate: Yup.string().required("Start date is required"),
  endDate: Yup.string().required("End date is required"),
  emiDate: Yup.string().required("EMI date is required"),
  provider: Yup.string().required("Loan provider is required"),
});

interface LoanFormProps {
  onSubmit: (values: LoanDetail) => Promise<LoanDetail | null>;
  onClose: () => void;
  initialData?: LoanDetail | null;
  selectedFiles: File[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  loanTypes: string[];
}

const LoanForm = ({
  onSubmit,
  onClose,
  initialData,
  selectedFiles,
  setSelectedFiles,
  loanTypes,
}: LoanFormProps) => {
  const { user } = useFirebaseAuth();
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedFile[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (initialData?.id && user) {
      fetchLoanDocuments();
    }
  }, [initialData?.id, user]);

  const fetchLoanDocuments = async () => {
    try {
      const docs = await fetchDocuments({
        documentContext: "vehicle",
        documentTypeId: initialData?.id || "",
        documentType: "vehicle-loan",
      });

      setUploadedDocuments(
        docs.map((doc) => ({
          id: doc.id,
          fileName: doc.fileName,
          presignedUrl: doc.presignedUrl,
        }))
      );
    } catch (error) {
      console.error("Error fetching loan documents:", error);
    }
  };

  const handlePreview = (url: string) => {
    window.open(url, "_blank");
  };

  const initialValues: LoanDetail = {
    type: initialData?.type || "",
    otherType: initialData?.otherType || "",
    totalCost: initialData?.totalCost || null,
    downPayment: initialData?.downPayment || null,
    amount: initialData?.amount || null,
    duration: initialData?.duration || null,
    emi: initialData?.emi || null,
    startDate: initialData?.startDate
      ? new Date(initialData.startDate).toISOString().split("T")[0]
      : "",
    endDate: initialData?.endDate
      ? new Date(initialData.endDate).toISOString().split("T")[0]
      : "",
    emiDate: initialData?.emiDate
      ? new Date(initialData.emiDate).toISOString().split("T")[0]
      : "",
    provider: initialData?.provider || "",
    vehicleCost: initialData?.vehicleCost || 0,
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={true}
    >
      {({
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        values,
        isValid,
        dirty,
      }: FormikProps<LoanDetail>) => (
        <Form>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12}>
                {!initialData?.id ? (
                  <FormControl
                    fullWidth
                    error={touched.type && Boolean(errors.type)}
                    size="small"
                  >
                    <InputLabel sx={{ fontSize: "0.75rem" }}>
                      Loan Type
                    </InputLabel>
                    <Select
                      label="Loan Type"
                      name="type"
                      value={values.type}
                      onChange={(e) => setFieldValue("type", e.target.value)}
                      {...commonTextFieldProps.SelectProps}
                    >
                      {loanTypes.map((type) => (
                        <MenuItem sx={{ fontSize: "0.75rem" }} value={type}>
                          {type === "chassis+body"
                            ? "Chassis + Body"
                            : type.charAt(0).toUpperCase() + type.slice(1)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <Field
                    as={TextField}
                    name="type"
                    label="Loan Type"
                    fullWidth
                    disabled
                    value={values.type.toUpperCase()}
                    {...commonTextFieldProps}
                  />
                )}
              </Grid>

              {values.type === "other" && !initialData?.id && (
                <Grid item xs={12} sm={12}>
                  <Field
                    as={TextField}
                    name="otherType"
                    label="Please Specify Loan Type"
                    fullWidth
                    error={touched.otherType && Boolean(errors.otherType)}
                    helperText={touched.otherType && errors.otherType}
                    {...commonTextFieldProps}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={12}>
                <Field
                  as={TextField}
                  name="totalCost"
                  label="Total Cost"
                  value={
                    values?.totalCost
                      ? Number(values.totalCost).toLocaleString("en-IN")
                      : ""
                  }
                  fullWidth
                  error={touched?.totalCost && Boolean(errors?.totalCost)}
                  helperText={touched?.totalCost && errors?.totalCost}
                  InputProps={{
                    startAdornment: <span style={{ marginRight: 8 }}>₹</span>,
                    inputProps: {
                      style: { textAlign: "left" },
                      onWheel: (e: React.WheelEvent<HTMLInputElement>) =>
                        (e.target as HTMLInputElement).blur(),
                    },
                  }}
                  {...commonTextFieldProps}
                  sx={{
                    ...commonTextFieldProps.sx,
                    "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
                      {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value.replace(/,/g, "");
                    setFieldValue("totalCost", value);
                    if (values?.downPayment && value && !isNaN(Number(value))) {
                      const amount = Number(value) - Number(values.downPayment);
                      setFieldValue("amount", amount > 0 ? amount : 0);
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name="provider"
                  label="Loan Provider"
                  fullWidth
                  error={touched?.provider && Boolean(errors?.provider)}
                  helperText={touched?.provider && errors?.provider}
                  {...commonTextFieldProps}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name="downPayment"
                  label="Down Payment"
                  value={
                    values?.downPayment && values.downPayment > 0
                      ? Number(values.downPayment).toLocaleString("en-IN")
                      : ""
                  }
                  fullWidth
                  error={touched?.downPayment && Boolean(errors?.downPayment)}
                  helperText={touched?.downPayment && errors?.downPayment}
                  InputProps={{
                    startAdornment: <span style={{ marginRight: 8 }}>₹</span>,
                    inputProps: {
                      style: { textAlign: "left" },
                      onWheel: (e: React.WheelEvent<HTMLInputElement>) =>
                        (e.target as HTMLInputElement).blur(),
                    },
                  }}
                  {...commonTextFieldProps}
                  sx={{
                    ...commonTextFieldProps.sx,
                    "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
                      {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                  }}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value.replace(/,/g, "");
                    setFieldValue("downPayment", value);
                    if (values?.totalCost && value && !isNaN(Number(value))) {
                      setFieldValue(
                        "amount",
                        Number(values.totalCost) - Number(value)
                      );
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name="amount"
                  label="Loan Amount"
                  value={
                    values?.amount && values.amount > 0
                      ? Number(values.amount).toLocaleString("en-IN")
                      : ""
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value.replace(/,/g, "");
                    setFieldValue("amount", value);
                    // if (values?.totalCost && value && !isNaN(Number(value))) {
                    //   setFieldValue(
                    //     "downPayment",
                    //     Number(values.totalCost) - Number(value)
                    //   );
                    // }
                  }}
                  fullWidth
                  error={touched?.amount && Boolean(errors?.amount)}
                  helperText={touched?.amount && errors?.amount}
                  InputProps={{
                    startAdornment: <span style={{ marginRight: 8 }}>₹</span>,
                    inputProps: {
                      style: { textAlign: "left" },
                      onWheel: (e: React.WheelEvent<HTMLInputElement>) =>
                        (e.target as HTMLInputElement).blur(),
                    },
                  }}
                  {...commonTextFieldProps}
                  sx={{
                    ...commonTextFieldProps.sx,
                    "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
                      {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name="duration"
                  label="Loan Duration (months)"
                  type="number"
                  fullWidth
                  error={touched?.duration && Boolean(errors?.duration)}
                  helperText={touched?.duration && errors?.duration}
                  {...commonTextFieldProps}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name="emi"
                  label="EMI"
                  fullWidth
                  value={
                    values?.emi && values.emi > 0
                      ? Number(values.emi).toLocaleString("en-IN")
                      : ""
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value.replace(/,/g, "");
                    setFieldValue("emi", value);
                  }}
                  error={touched?.emi && Boolean(errors?.emi)}
                  helperText={touched?.emi && errors?.emi}
                  InputProps={{
                    startAdornment: <span style={{ marginRight: 8 }}>₹</span>,
                    inputProps: {
                      style: { textAlign: "left" },
                      onWheel: (e: React.WheelEvent<HTMLInputElement>) =>
                        (e.target as HTMLInputElement).blur(),
                    },
                  }}
                  {...commonTextFieldProps}
                  sx={{
                    ...commonTextFieldProps.sx,
                    "& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button":
                      {
                        "-webkit-appearance": "none",
                        margin: 0,
                      },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name="emiDate"
                  label="EMI Start Date"
                  type="date"
                  fullWidth
                  {...commonDateFieldProps}
                  error={touched?.emiDate && Boolean(errors?.emiDate)}
                  helperText={touched?.emiDate && errors?.emiDate}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name="startDate"
                  label="Loan Start Date"
                  type="date"
                  fullWidth
                  {...commonDateFieldProps}
                  error={touched?.startDate && Boolean(errors?.startDate)}
                  helperText={touched?.startDate && errors?.startDate}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValue("startDate", e.target.value);
                    if (values?.duration) {
                      const startDate = new Date(e.target.value);
                      const endDate = new Date(startDate);
                      endDate.setMonth(endDate.getMonth() + values.duration);
                      setFieldValue(
                        "endDate",
                        endDate.toISOString().split("T")[0]
                      );
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field
                  as={TextField}
                  name="endDate"
                  label="Loan End Date"
                  type="date"
                  fullWidth
                  {...commonDateFieldProps}
                  error={touched?.endDate && Boolean(errors?.endDate)}
                  helperText={touched?.endDate && errors?.endDate}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <DocumentUploadSection
                  title="Loan Documents"
                  selectedFiles={selectedFiles}
                  uploadedFiles={uploadedDocuments}
                  onFileSelect={(files: File[]) =>
                    setSelectedFiles((prev) => [...prev, ...files])
                  }
                  onFileRemove={(index: number) =>
                    setSelectedFiles((prev) =>
                      prev.filter((_, i) => i !== index)
                    )
                  }
                  onPreview={handlePreview}
                  isLoading={isLoading}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} {...commonButtonProps}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting || !isValid || !dirty}
              {...commonButtonProps}
            >
              Submit
            </Button>
          </DialogActions>
        </Form>
      )}
    </Formik>
  );
};

export default LoanForm;
